/**
 * RecipeShelf
 *
 * Horizontal scrollable shelf section with serif header and optional action link.
 */

import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  ViewStyle,
  TextStyle,
} from 'react-native';
import { Paprika, PaprikaTypography } from '../../styles/theme';

interface RecipeShelfProps {
  title: string;
  icon?: React.ReactNode;
  action?: string; // e.g. "Tout voir" or "12 dispo"
  onActionPress?: () => void;
  /** How many recipes the row holds, drawn after the title (search rows, #847). */
  count?: number;
  children: React.ReactNode;
}

export const RecipeShelf: React.FC<RecipeShelfProps> = ({
  title,
  icon,
  action,
  onActionPress,
  count,
  children,
}) => {
  return (
    <View style={styles.root}>
      {/* Header Row */}
      <View style={styles.headerRow}>
        <View style={styles.titleContainer}>
          {!!icon && <View style={styles.iconContainer}>{icon}</View>}
          <Text style={styles.title}>{title}</Text>
          {count != null && <Text style={styles.count}>{count}</Text>}
        </View>

        {!!action && onActionPress && (
          <TouchableOpacity onPress={onActionPress} activeOpacity={0.7}>
            <Text style={styles.action}>{action}</Text>
          </TouchableOpacity>
        )}
      </View>

      {/* Horizontal ScrollView */}
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={styles.scrollContent}
      >
        {children}
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  root: {
    backgroundColor: Paprika.background,
  } as ViewStyle,
  headerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 18,
    marginBottom: 12,
  } as ViewStyle,
  titleContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  } as ViewStyle,
  iconContainer: {
    marginRight: 8,
  } as ViewStyle,
  title: {
    fontFamily: 'Newsreader-Regular',
    fontSize: 17,
    color: Paprika.textPrimary,
  } as TextStyle,
  count: {
    fontFamily: 'Inter-Regular',
    fontSize: 13,
    color: Paprika.textMuted,
    marginLeft: 6,
  } as TextStyle,
  action: {
    fontFamily: 'Inter-Medium',
    fontSize: 13,
    fontWeight: '600',
    color: Paprika.accent,
  } as TextStyle,
  scrollContent: {
    paddingHorizontal: 18,
    gap: 13,
  } as ViewStyle,
});

export default RecipeShelf;
